/* eslint-disable no-unused-vars */

import { get } from 'lodash';
import moment from 'moment';
import { email, between, required, numeric } from 'vuelidate/lib/validators';
import { withParams, req } from 'vuelidate/lib/validators/common';
import { isValidPhoneNumber } from 'libphonenumber-js';
import constants from '@/constants';
import { getCardTypeForNumber } from '@/utils/helpers';

const COUNTRY_CODE_US = 'US';


const hasOneDigit = withParams({ type: 'hasOneDigit' }, value => /\d/.test(value));
const hasOneUpper = withParams({ type: 'hasOneUpper' }, value => /[A-Z]/.test(value));
const hasOneLower = withParams({ type: 'hasOneLower' }, value => /[a-z]/.test(value));
const usernameIsValid = withParams({ type: 'usernameIsValid' }, value => /^[a-zA-Z0-9_]+$/.test(value));
const noSpaces = withParams({ type: 'noSpaces' }, value => value.indexOf(' ') === -1);
const minLength = min => withParams({ type: 'minLength', min }, value => value.length >= min);
const maxLength = max => withParams({ type: 'maxLength', max }, value => value.length <= max);
const minLengthDigit = min => withParams({ type: 'minLengthDigit', min }, value => value.length >= min);
const maxLengthDigit = max => withParams({ type: 'maxLengthDigit', max }, value => value.length <= max);
const exactLength = target => withParams({ type: 'exactLength', target }, value => value.length === target);
const notEmptyMessage = withParams({ type: 'notEmptyMessage' }, value => /\S\w*/.test(value));
const onlyCharacters = withParams({ type: 'onlyCharacters' }, value => /^[A-Za-záéíóú\s'`’.,\-()/]*$/.test(value));
const requiredNoMessage = withParams({ type: 'requiredNoMessage' }, req);
const emailNoMessage = withParams({ type: 'emailNoMessage' }, email);
const price = withParams({ type: 'price' }, (value) => {
  if (value && value.length > 1 && value.startsWith('0')) return false;
  return /^\d+\.?\d{0,2}$/.test(value);
});

const priceAllowZero = withParams({ type: 'price' }, (value) => {
  if (value && value.length > 1 && value.startsWith('.')) return /^\d+$/.test(value.slice(1));
  return /^\d+\.?\d{0,2}$/.test(value);
});

const multipleEmails = withParams({ type: 'multipleEmails' }, (value) => {
  const emails = value.split(';').map(i => i.replace(' ', ''));
  let isValid = true;
  emails.forEach((val) => {
    if (!email(val)) {
      isValid = false;
    }
  });
  return isValid;
});

const minValue = min => withParams(
  { type: 'minValue', min }, value =>
  !req(value) || ((!/\s/.test(value) || value instanceof Date) && +value >= +min),
);

const greaterThan = min => withParams(
  { type: 'greaterThan', min }, value =>
  !req(value) || ((!/\s/.test(value) || value instanceof Date) && +value > +min),
);

const minValueDeposit = min => withParams(
  { type: 'minValueDeposit', min }, value =>
  !req(value) || ((!/\s/.test(value) || value instanceof Date) && +value >= +min),
);

const maxValueDeposit = max => withParams(
  { type: 'maxValueDeposit', max }, value =>
  !req(value) || ((!/\s/.test(value) || value instanceof Date) && +value <= +max),
);

// this one should work with "fields.email" built in does not
const sameAs = param => withParams({ type: 'sameAs' }, (value, parentVm) => {
  const targetParam = parentVm[param];
  return value.toLowerCase() === targetParam.toLowerCase();
});

const validateIsAdult = (parMonth, parDay) => withParams({ type: 'validateIsAdult' }, (value, parentVm) => {
  const yearSelect = value || {};
  const monthSelect = parentVm[parMonth] || {};
  const daySelect = parentVm[parDay] || {};
  if (yearSelect.value && monthSelect.value && daySelect.value) {
    const dateOfBirth = new Date(yearSelect.value, monthSelect.value - 1, daySelect.value);
    return moment().diff(moment(dateOfBirth), 'years') >= 18;
  }
  return true;
});

const customPattern = (parPattern, parMessage) => withParams({ type: '' }, value => (new RegExp(parPattern)).test(value));

const validatenNscbStatus = parCountry => withParams({ type: 'validatenNscbStatus' }, (value, parentVm) => {
  const countrySelect = parentVm[parCountry];
  if (countrySelect.value !== COUNTRY_CODE_US && !value) {
    return false;
  }
  return true;
});

const validateSSN = parCountry => withParams({ type: 'validateSSN' }, (value, parentVm) => {
  const countrySelect = parentVm[parCountry];
  if (countrySelect.value === COUNTRY_CODE_US) {
    return /^\d{4}$/.test(value);
  }
  return true;
});

const validateUSZipCode = parCountry => withParams({ type: 'validateUSZipCode' }, (value, parentVm) => {
  const countrySelect = parentVm[parCountry];
  const isFieldRequired = get(countrySelect, 'required', true);

  if (!countrySelect || !countrySelect.value) {
    return true;
  }

  if (isFieldRequired && countrySelect.value === COUNTRY_CODE_US) {
    return /^[0-9\s]*$/.test(value) && value.length === 5;
  }

  if (isFieldRequired && value.length === 0) {
    return false;
  }

  return true;
});

const validateCardNumber = withParams({ type: 'validateCardNumber' }, (value, parentVm) => {
  const trimmedValue = (value || '').replace(/ /g, '');
  const cardType = getCardTypeForNumber(trimmedValue);
  return cardType !== constants.CARDS.BLANK;
});

// Phone validator - required field, defaults to US if no country code prefix
const phone = withParams({ type: 'phone' }, (value) => {
  if (!value) return false;
  // If starts with +, validate as international; otherwise default to US
  if (value.startsWith('+')) {
    return isValidPhoneNumber(value);
  }
  return isValidPhoneNumber(value, 'US');
});

// Phone validator - optional field, validates only if not empty
const phoneOptional = withParams({ type: 'phoneOptional' }, (value) => {
  if (!value || value.trim() === '') return true; // Allow empty
  // If starts with +, validate as international; otherwise default to US
  if (value.startsWith('+')) {
    return isValidPhoneNumber(value);
  }
  return isValidPhoneNumber(value, 'US');
});

export default {
  withdrawalAmount: (parFrom, parTo) => ({
    price,
    required,
    between: between(parFrom, parTo),
  }),
  depositAmount: (parFrom, parTo) => ({
    price,
    required,
    minValueDeposit: minValueDeposit(parFrom),
    maxValueDeposit: maxValueDeposit(parTo),
  }),
  depositAmountCoinbase: (parFrom, parTo) => ({
    price: priceAllowZero,
    required,
    minValueDeposit: minValueDeposit(parFrom),
    maxValueDeposit: maxValueDeposit(parTo),
  }),
  onlyCharacters: {
    required,
    onlyCharacters,
  },
  cardNumber: {
    validateCardNumber,
  },
  taxId: {
    required,
    exactLength: exactLength(9),
  },
  required: {
    required,
  },
  email: {
    required,
    email,
  },
  emailConfirmation: par => ({
    required,
    sameAs: sameAs(par),
  }),
  validateIsAdult: (parMonth, parDay) => ({
    required,
    validateIsAdult: validateIsAdult(parMonth, parDay),
  }),
  validateUSZipCode: parCountry => ({
    validateUSZipCode: validateUSZipCode(parCountry),
  }),
  validatenNscbStatus: parCountry => ({
    validatenNscbStatus: validatenNscbStatus(parCountry),
  }),
  validateSSN: parCountry => ({
    validateSSN: validateSSN(parCountry),
  }),
  password: {
    required,
    minLength: minLength(6),
    hasOneDigit,
    hasOneLower,
    hasOneUpper,
  },
  passwordConfirmation: par => ({
    required,
    // minLength: minLength(6),
    // hasOneDigit,
    // hasOneLower,
    // hasOneUpper,
    sameAs: sameAs(par),
  }),
  username: {
    required,
    noSpaces,
    usernameIsValid,
    minLength: minLength(3),
    maxLength: maxLength(14),
  },
  message: {
    required,
    notEmptyMessage,
  },
  requiredNoMessage: { // not mapped in validatorsMessages so error does not display
    requiredNoMessage,
  },
  emailNoMessage: { // not mapped in validatorsMessages so error does not display
    emailNoMessage,
  },
  cvv: {
    required,
    numeric,
    minLengthDigit: minLengthDigit(3),
    maxLengthDigit: maxLengthDigit(4),
  },
  accountNumber: {
    required,
    numeric,
    minValue: greaterThan(0),
  },
  routingNumber: {
    required,
    numeric,
    exactLength: exactLength(9),
  },
  multipleEmails: {
    required,
    multipleEmails,
  },
  customPattern: (parPattern, parMessage) => ({
    customPattern: customPattern(parPattern, parMessage),
  }),
  captcha: {
    required,
  },
  phone: {
    required,
    phone,
  },
  phoneOptional: {
    phoneOptional,
  },
};



// WEBPACK FOOTER //
// ./src/helpers/validators.js